"use client";

import * as React from "react";
import { Maximize2 } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
const galleryImages = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/outdoor_kitchen_nj.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/outdoor_kitchen_nj.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/outdoor_kitchen_nj.png-wvc-sizes",
  alt: "Custom outdoor kitchen with ClifRock panel masonry system",
  title: "Outdoor Kitchen, NJ"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/luxury_pool_waterfall.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/luxury_pool_waterfall.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/luxury_pool_waterfall.png-wvc-sizes",
  alt: "Large scale pool waterfall feature built with lightweight panels",
  title: "Pool Waterfall, FL"
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/backyard_fire_pit.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/backyard_fire_pit.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/backyard_fire_pit.png-wvc-sizes",
  alt: "Modern stone fire pit integrated into a patio design",
  title: "Fire Feature, TX"
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/retaining_wall_system.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/retaining_wall_system.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/retaining_wall_system.png-wvc-sizes",
  alt: "Structural retaining wall with natural stone texture",
  title: "Retaining Wall, CA"
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/outdoor_living_pavilion.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/outdoor_living_pavilion.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/outdoor_living_pavilion.png-wvc-sizes",
  alt: "Complete outdoor living space with kitchen and seating",
  title: "Living Pavilion, IL"
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/commercial_hardscape.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/commercial_hardscape.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/commercial_hardscape.png-wvc-sizes",
  alt: "Commercial grade hardscaping for a resort entrance",
  title: "Resort Entrance, NV"
}];
export default function MasterpieceProjectGallery() {
  return <section data-section-id="258246" id="masterpiece-project-gallery" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 max-w-3xl">
          <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground mb-6 uppercase tracking-tight border-b-4 border-primary inline-block pb-2">
            Project Gallery
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-default">
            Explore our wall of excellence. See how ClifRock installers are transforming backyards with faster installations and premium masonry aesthetics.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {galleryImages.map((image, i) => <div key={image.id} data-index={i}>
              <Dialog>
                <DialogTrigger asChild>
                  <button className="group relative block w-full overflow-hidden rounded-lg bg-muted transition-all duration-300 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
                    <AspectRatio ratio={4 / 3}>
                      <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                      <div className="absolute inset-0 bg-primary/60 opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex items-center justify-center backdrop-blur-sm">
                        <div className="bg-background rounded-lg px-4 py-2 text-foreground flex items-center gap-2 shadow-md">
                          <Maximize2 className="w-5 h-5 text-primary" />
                          <span className="font-default font-bold uppercase text-sm tracking-wider">
                            {image.title}
                          </span>
                        </div>
                      </div>
                    </AspectRatio>
                  </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-5xl p-2 border-2 border-border bg-background rounded-lg">
                  <DialogHeader className="sr-only">
                    <DialogTitle>{image.title}</DialogTitle>
                    <DialogDescription>{image.alt}</DialogDescription>
                  </DialogHeader>
                  <div className="relative w-full h-full min-h-[50vh] max-h-[85vh] flex items-center justify-center bg-muted rounded-md overflow-hidden">
                    <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} className="max-w-full max-h-[80vh] object-contain" loading="lazy" />
                    <div className="absolute bottom-0 left-0 right-0 bg-background/95 border-t border-border p-4 backdrop-blur-md">
                      <h3 className="font-default font-bold text-lg text-foreground uppercase">
                        {image.title}
                      </h3>
                      <p className="text-muted-foreground text-sm mt-1 font-default">
                        {image.alt}
                      </p>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>
      </div>
    </section>;
}